import { useState, useRef, useEffect, useMemo } from 'react';
import { MdExpandMore, MdCheck, MdSearch } from 'react-icons/md';
import useClubVocab from '../../hooks/useClubVocab';

// Reusable searchable single-select for picking a student. Type to filter by name or
// member id; keyboard + outside-click friendly; theme-aware (semantic tokens, no
// Tailwind dark:). Replaces native <select> so there's no OS popup. The submitted
// value is whatever getOptionValue returns — callers control it, so nothing downstream
// changes vs. the old native selects.

// Defaults match the app's flexible student shapes (StudentProfile with nested user, or flat).
const defGetValue = (s) => s?.user?._id || s?.user || s?._id || '';
const defGetLabel = (s) =>
  s?.user?.name || s?.fullName || s?.name ||
  [s?.firstName, s?.lastName].filter(Boolean).join(' ').trim() ||
  s?.email || s?.studentId || 'Unnamed student';
const defGetMeta = (s) => s?.studentId || s?.studentCode || s?.code || s?.registrationId || '';

function StudentSelect({
  students = [],
  value = '',
  onChange,
  placeholder,
  allowAll = false,
  allOptionLabel,
  getOptionValue = defGetValue,
  getOptionLabel = defGetLabel,
  getOptionMeta = defGetMeta,
  // Optional: widen what typing matches beyond label+meta (e.g. include phone). Defaults to
  // label+meta so every existing caller that doesn't pass this filters exactly as before.
  getOptionSearchText,
  disabled = false,
  loading = false,
  error = false,
  className = '',
}) {
  const v = useClubVocab();
  // Vocab-aware fallbacks so a gym reads "Member" while callers can still override the wording.
  const ph       = placeholder    ?? `Select a ${v.member.toLowerCase()}`;
  const allLabel = allOptionLabel ?? `All ${v.members.toLowerCase()}`;
  const [open, setOpen]           = useState(false);
  const [query, setQuery]         = useState('');
  const [highlight, setHighlight] = useState(0);
  const rootRef  = useRef(null);
  const inputRef = useRef(null);
  const listRef  = useRef(null);

  const options = useMemo(
    () => students.map(s => ({
      id:   getOptionValue(s),
      label: getOptionLabel(s),
      meta: getOptionMeta(s),
      // Pre-lowercased once per student, not per keystroke.
      searchText: (getOptionSearchText ? getOptionSearchText(s) : `${getOptionLabel(s)} ${getOptionMeta(s)}`).toLowerCase(),
    })),
    [students, getOptionValue, getOptionLabel, getOptionMeta, getOptionSearchText],
  );

  const q = query.trim().toLowerCase();
  const filtered = useMemo(
    () => q ? options.filter(o => o.searchText.includes(q)) : options,
    [options, q],
  );

  // "All students" clear row sits on top when allowed and not actively searching.
  const showAllRow = allowAll && !q;
  const rows = showAllRow
    ? [{ id: '', label: allLabel, meta: '', isAll: true }, ...filtered]
    : filtered;

  const selected      = value ? options.find(o => String(o.id) === String(value)) : null;
  const triggerLabel  = selected ? selected.label : (allowAll ? allLabel : ph);
  const triggerMeta   = selected ? selected.meta : '';
  const isPlaceholder = !selected && !allowAll;

  // On open: clear search, reset highlight, focus the input.
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setHighlight(0);
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [open]);

  // Outside-click closes.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // Keep highlight in range and scrolled into view.
  useEffect(() => { setHighlight(h => Math.min(h, Math.max(0, rows.length - 1))); }, [rows.length]);
  useEffect(() => {
    if (open) listRef.current?.children?.[highlight]?.scrollIntoView?.({ block: 'nearest' });
  }, [highlight, open]);

  const pick = (row) => {
    onChange?.(row.isAll ? '' : row.id);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown')      { e.preventDefault(); setHighlight(h => Math.min(h + 1, rows.length - 1)); }
    else if (e.key === 'ArrowUp')   { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter')     { e.preventDefault(); if (rows[highlight]) pick(rows[highlight]); }
    else if (e.key === 'Escape')    { e.preventDefault(); setOpen(false); }
  };

  const triggerCls = `w-full flex items-center justify-between gap-2 bg-card border rounded-lg px-3 py-2 text-sm text-left transition-colors focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary disabled:opacity-50 ${
    error ? 'border-red-500' : 'border-divider'
  }`;

  return (
    <div className={`relative ${className}`} ref={rootRef}>
      <button
        type="button"
        onClick={() => !disabled && setOpen(o => !o)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={triggerCls}
      >
        <span className="min-w-0 truncate flex items-center gap-1.5">
          <span className={isPlaceholder ? 'text-muted' : 'text-secondary'}>
            {loading ? `Loading ${v.members.toLowerCase()}…` : triggerLabel}
          </span>
          {triggerMeta && <span className="text-muted text-xs truncate">({triggerMeta})</span>}
        </span>
        <MdExpandMore className={`text-muted text-lg flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-card border border-divider rounded-lg shadow-xl overflow-hidden">
          {/* Search */}
          <div className="relative border-b border-divider">
            <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => { setQuery(e.target.value); setHighlight(0); }}
              onKeyDown={onKeyDown}
              placeholder="Type a name…"
              className="w-full bg-card pl-9 pr-3 py-2 text-sm text-secondary placeholder-gray-400 focus:outline-none"
            />
          </div>

          {/* List */}
          <div ref={listRef} role="listbox" className="max-h-60 overflow-y-auto py-1">
            {rows.length === 0 ? (
              <p className="px-3 py-3 text-sm text-muted text-center">No {v.members.toLowerCase()} found</p>
            ) : (
              rows.map((row, i) => {
                const isSel = row.isAll ? !value : String(row.id) === String(value);
                const isHi  = i === highlight;
                return (
                  <button
                    key={row.isAll ? '__all__' : row.id}
                    type="button"
                    role="option"
                    aria-selected={isSel}
                    onMouseEnter={() => setHighlight(i)}
                    onClick={() => pick(row)}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors hover:bg-nav-hover ${isHi ? 'bg-nav-hover' : ''}`}
                  >
                    <span className="flex-1 min-w-0 truncate">
                      <span className={`${row.isAll ? 'text-muted' : 'text-secondary'} ${isSel ? 'font-medium' : ''}`}>
                        {row.label}
                      </span>
                      {row.meta && <span className="text-muted text-xs ml-1.5">({row.meta})</span>}
                    </span>
                    {isSel && <MdCheck className="text-primary text-base flex-shrink-0" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default StudentSelect;
